// Imports
import { saveBlobAsFile } from '@/api/apiUtils'
import { DownloadIcon, SearchIconBtn } from '@/assets/icons'
import { ConfirmationDialog, Search } from '@/components/common'
import CustomButton from '@/components/Returns/CustomButton'
import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { MT_ASSIGN_ENTITY_COLUMNS } from '@/multi-territory/mt-configs/mt-assign-entity'
import { COUNTRY_OPTIONS } from '@/multi-territory/mt-constants/mt-country-default'
import {
  createAssignEntity,
  deleteAssignEntity,
  downloadAssignEntity,
  getAssignEntityCountryDropdown,
  getAssignEntityList,
} from '@/multi-territory/mt-services/mt-assign-entity-api'
import type {
  MTAssignEntityCreateRequest,
  MTAssignEntityListItem,
  MTCountryOption,
} from '@/multi-territory/mt-types/mt-admin'
import { MTMetadataTableColumn } from '@/multi-territory/mt-types/mt-metadata'
import {
  buildMTSearchField,
  formatMTHeaderLabel,
  formatMTSearchCriteria,
} from '@/multi-territory/mt-utils/mt-search-fields'
import { ROLE_CAPABILITIES } from '@/utils/role-capabilities'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { MTCommonTable } from '../mt-shared/MTCommonTable'
import { MTAdminAssignEntityForm } from './MTAdminAssignEntityForm'

const AssignEntitySearch = Search as any

// Columns that must NOT appear as advanced-search filter options on the
// Assign Entity (multi-country) grid. "User name" (`name`) is excluded to match
// the India behaviour — the grid spans multiple countries, so these fields
// can't be filtered meaningfully per-request and previously returned 0 rows.
// A column is non-searchable if its id is listed here OR it has
// `searchable: false` in the column config.
const ASSIGN_ENTITY_NON_SEARCHABLE_FIELDS = ['userId', 'name']

const isAssignEntitySearchable = (col: {
  id: string
  searchable?: boolean
}): boolean =>
  col.searchable !== false &&
  !ASSIGN_ENTITY_NON_SEARCHABLE_FIELDS.includes(col.id)

interface CountryOptionUI {
  uuid: string
  countryCode: string
  countryName: string
  taxRegistrationRequired?: boolean
}

export function MTAdminAssignEntity() {
  // Hooks
  const roleTypes = useSelector((state: any) => state.user.role)
  const { showSnackbar } = useSnackbar()
  const reduxCountryOptions: MTCountryOption[] =
    useSelector((state: any) => state.navigation.countryOptions) ||
    COUNTRY_OPTIONS

  // State
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    GLOBAL_CONSTANTS.DEFAULT_PAGE_SIZE
  )
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState<Record<
    string,
    unknown
  > | null>(null)

  // Country options (fetched for the form modal)
  const [countryOptions, setCountryOptions] = useState<CountryOptionUI[]>(
    COUNTRY_OPTIONS.map(item => ({
      uuid: item.uuid,
      countryCode: item.country_code,
      countryName: item.country_name,
    }))
  )

  // Assign entity listing data
  const [assignEntityData, setAssignEntityData] = useState<
    MTAssignEntityListItem[]
  >([])

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Record<
    string,
    unknown
  > | null>(null)

  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: MT_ASSIGN_ENTITY_COLUMNS.find(isAssignEntitySearchable)?.id || '',
      value: '',
    },
  ])
  const [activeSearchList, setActiveSearchList] = useState<any[]>([])

  // Derived Data
  const roleConfig =
    ROLE_CAPABILITIES[roleTypes as keyof typeof ROLE_CAPABILITIES] ||
    ROLE_CAPABILITIES.default

  const hasFetchedRef = useRef(false)

  // Fetch country dropdown and listing on mount
  useEffect(() => {
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true
    fetchCountryDropdown()
    fetchAssignEntityList(1, itemsPerPage)
  }, [])

  const fetchCountryDropdown = async () => {
    try {
      const response = await getAssignEntityCountryDropdown()
      if (
        response.success &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        const mapped: CountryOptionUI[] = response.data.map((item: any) => ({
          uuid: item.uuid,
          countryCode: item.country_code,
          countryName: item.country_name,
          taxRegistrationRequired: item.tax_registration_required,
        }))
        setCountryOptions(mapped)
      }
    } catch {
      // Use fallback COUNTRY_OPTIONS
    }
  }

  const fetchAssignEntityList = async (
    page = 1,
    pageSize = itemsPerPage,
    searchList: any[] = []
  ) => {
    setLoading(true)
    try {
      const payload = {
        pageFilter: {
          currentPage: page,
          pageSize: pageSize,
          sortField: 'createdDate',
          sortOrder: 'DESC',
          includeCount: true,
        },
        searchCOList: searchList,
      }
      const response = await getAssignEntityList(payload)
      if (response.success) {
        const content = response.data?.content || []
        setAssignEntityData(content)
        setTotalItems(response.data?.totalElements || 0)
        setCurrentPage(page)
      } else if (response.status !== 403) {
        // Suppress snackbar for 403 (Forbidden) - access is handled at the UI level
        showSnackbar(
          response.error || 'Failed to fetch assign entity list',
          'error'
        )
        setAssignEntityData([])
        setTotalItems(0)
      } else {
        setAssignEntityData([])
        setTotalItems(0)
      }
    } catch {
      setAssignEntityData([])
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }

  // Handlers
  const handlePageChange = (page: number): void => {
    setCurrentPage(page)
    fetchAssignEntityList(page, itemsPerPage)
  }

  const handleItemsPerPageChange = (size: number): void => {
    setItemsPerPage(size)
    setCurrentPage(1)
    fetchAssignEntityList(1, size)
  }

  const handleDelete = (row: Record<string, unknown>): void => {
    setDeleteTarget(row)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteTarget?.uuid) return
    try {
      const response = await deleteAssignEntity([deleteTarget.uuid as string])
      if (response.success) {
        showSnackbar(response.data || 'Deleted successfully', 'success')
        fetchAssignEntityList(currentPage, itemsPerPage)
      } else {
        showSnackbar(response.error || 'Failed to delete', 'error')
      }
    } catch {
      showSnackbar('Error deleting assignment', 'error')
    } finally {
      setDeleteDialogOpen(false)
      setDeleteTarget(null)
    }
  }

  const handleAdd = (): void => {
    setEditingAssignment(null)
    setModalOpen(true)
  }

  const handleSearchClose = (): void => {
    setSearchOpen(false)
    setSearchAnchorEl(null)
  }

  const handleApplySearch = async (searchList: any[]): Promise<void> => {
    const apiCriteria = formatMTSearchCriteria(searchList)
    const mappedCriteria = apiCriteria.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setActiveSearchList(apiCriteria)
    setCurrentPage(1)
    setSearchOpen(false)
    setSearchAnchorEl(null)
    await fetchAssignEntityList(1, itemsPerPage, apiCriteria)
  }

  const handleResetSearch = async (): Promise<void> => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field:
          MT_ASSIGN_ENTITY_COLUMNS.find(isAssignEntitySearchable)?.id || '',
        value: '',
      },
    ])
    setActiveSearchList([])
    setCurrentPage(1)
    setSearchOpen(false)
    setSearchAnchorEl(null)
    await fetchAssignEntityList(1, itemsPerPage, [])
  }

  const handleDownload = async (): Promise<void> => {
    if (totalItems === 0) {
      showSnackbar('No data to download', 'info')
      return
    }

    try {
      const payload = {
        pageFilter: null,
        searchCOList: activeSearchList,
      }

      const response = await downloadAssignEntity(payload)

      if (response.success) {
        saveBlobAsFile(response.data, 'assign-entity.xlsx')
      } else {
        showSnackbar('Failed to download file', 'error')
      }
    } catch (error) {
      console.error('Download error:', error)
      showSnackbar('Error downloading file', 'error')
    }
  }

  const handleModalClose = (): void => {
    setModalOpen(false)
    setEditingAssignment(null)
  }

  const handleFormSubmit = async (formData: {
    userId: number
    entityTanMasterDTOs: MTAssignEntityCreateRequest['entityTanMasterDTOs']
  }): Promise<void> => {
    const payload: MTAssignEntityCreateRequest = {
      entityTanMasterDTOs: formData.entityTanMasterDTOs,
    }

    const response = await createAssignEntity(payload)
    if (response.success) {
      showSnackbar(response.data || 'Entity assigned successfully', 'success')
      fetchAssignEntityList(1, itemsPerPage)
    } else {
      throw new Error(response.error || 'Failed to assign entity')
    }

    setModalOpen(false)
    setEditingAssignment(null)
  }

  // Table columns for MTCommonTable (auto width, capped at 200px, wraps beyond).
  // Only a delete action is exposed here (edit was disabled via showEdit=false).
  const tableColumns = useMemo<MTMetadataTableColumn[]>(() => {
    const base = MT_ASSIGN_ENTITY_COLUMNS.map(col => ({
      id: col.id,
      label: col.label,
      type: 'text' as const,
      sortable: false,
      editable: false,
      // Tax identification number: show TAN when applicable, else the entity PAN.
      ...(col.id === 'taxDeductionAccountNumber'
        ? {
            render: (_value: unknown, row?: Record<string, unknown>) =>
              row?.tanApplicable
                ? (row?.taxDeductionAccountNumber ?? '')
                : (row?.entityPan ?? ''),
          }
        : {}),
    }))
    if (!roleConfig.canEdit) return base
    return [
      ...base,
      {
        id: '_action',
        label: 'Actions',
        type: 'text' as const,
        sortable: false,
        editable: false,
        width: '120px',
      },
    ]
  }, [roleConfig.canEdit])

  const handleRowDelete = (uuid: string): void => {
    const row = assignEntityData.find(r => (r as any).uuid === uuid)
    if (row) handleDelete(row as unknown as Record<string, unknown>)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mt: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-xl)',
            fontWeight: 500,
            lineHeight: '1.3em',
            color: 'var(--forbrand-secondary)',
          }}
        >
          {GLOBAL_CONSTANTS.MT_ADMIN_ASSIGN_ENTITY_TITLE}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Tooltip title="Download Excel">
            <IconButton size="medium" onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Search">
            <IconButton
              size="medium"
              onClick={e => {
                setSearchAnchorEl(e.currentTarget)
                setSearchOpen(true)
              }}
            >
              <SearchIconBtn />
            </IconButton>
          </Tooltip>
          {roleConfig.canEdit && (
            <>
              <Box
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--border-subtel)',
                }}
              />
              <CustomButton onClick={handleAdd}>
                {GLOBAL_CONSTANTS.MT_ADMIN_ASSIGN_ENTITY_ADD}
              </CustomButton>
            </>
          )}
        </Box>
      </Box>

      <MTCommonTable
        columns={tableColumns}
        data={assignEntityData}
        loading={loading}
        onRowDelete={roleConfig.canEdit ? handleRowDelete : undefined}
        showFooter={true}
        totalItems={totalItems}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        emptyMessage="No assignments found"
      />

      <MTAdminAssignEntityForm
        open={modalOpen}
        onClose={handleModalClose}
        editingAssignment={editingAssignment}
        onSubmit={handleFormSubmit}
        countryOptions={countryOptions}
        roleTypes={roleTypes}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        title="Delete Assignment"
        message={`Are you sure you want to delete this assignment for "${(deleteTarget as any)?.username || ''}"?`}
        confirmText="Delete User Assignment"
        type="warning"
        itemType="item"
        showCancelButton={true}
      />

      <AssignEntitySearch
        open={searchOpen}
        anchorEl={searchAnchorEl}
        onClose={handleSearchClose}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        fields={MT_ASSIGN_ENTITY_COLUMNS.filter(isAssignEntitySearchable).map(
          col => {
            if (col.id === 'countryName') {
              return {
                ...col,
                label: formatMTHeaderLabel(col.label),
                type: 'dropdown',
                placeholder: 'Select country',
                options: reduxCountryOptions.map(c => ({
                  label: c.country_name,
                  value: c.country_name,
                })),
              }
            }
            return buildMTSearchField(col)
          }
        )}
        placement="bottom-start"
        searchCriteria={searchCriteria}
        setSearchCriteria={setSearchCriteria}
      />
    </Box>
  )
}
